/**
 * The Screener: one card per sender you have never decided about.
 *
 * A STACK SOMEBODY CAN FINISH, which is the whole reason it is one card per
 * SENDER and not one per message. A second inbox is not a feature; a short pile
 * of yes/no questions that runs out is.
 *
 * THE FOURTH ANSWER IS NOT A FOURTH BUTTON, and that is the correction this
 * component exists to carry. shell-ia §6 specifies "four full-width buttons —
 * Yes → People, Newsletters, Receipts, No thanks (Trash future mail)", and
 * `review.md` traced what that shape does: the fourth is a standing rule that
 * permanently routes a sender's future mail to Trash, rendered with identical
 * weight, size and position-in-a-stack to three benign sorting choices. On a
 * phone that is one mis-tap from "Yes", and the consequence is silent — mail
 * from that sender stops arriving, for ever, with no trace in any inbox.
 *
 * So: three primary buttons, a hairline, then a text-weight link with a confirm
 * that names the sender and says where to undo it. The three sorting answers
 * stay one tap because they are recoverable and frequent; the destructive one
 * costs a second tap because it is neither.
 */
import { useCallback, useEffect, useState } from 'react'
import { api, type KnownSender, type PendingSender, type SenderDisposition } from './api.ts'
import { listDate } from './format.ts'
import { readScreenerEnabled } from './settings.ts'
// The scrim and the panel chrome: this surface renders
// `<div className="settings-panel screener-panel">` and has borrowed those two
// since it was written, getting away with it only because the settings panel is
// imported by the same shell. Its own rules — including `.screener-inert` — are
// in shell.css with the other thirty-six.
import './settings.css'

export interface ScreenerProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly onError: (message: string) => void
  /** Told when a decision lands, so the shell can re-run its walk. */
  readonly onDecided?: () => void
}

/** The three answers that sort, in the order the card offers them. */
const SORTING: readonly { readonly value: SenderDisposition; readonly label: string; readonly hint: string }[] = [
  { value: 'people', label: 'Yes — People', hint: 'Their mail lands in your Inbox.' },
  { value: 'news', label: 'Newsletters', hint: 'Filed with the rest of your reading.' },
  { value: 'receipts', label: 'Receipts', hint: 'Filed with orders and invoices.' },
]

const DISPOSITION_LABEL: Readonly<Record<SenderDisposition, string>> = {
  people: 'People',
  news: 'Newsletters',
  receipts: 'Receipts',
  trash: 'Trash',
}

export function Screener({ open, onClose, onError, onDecided }: ScreenerProps): React.JSX.Element | null {
  const [senders, setSenders] = useState<readonly PendingSender[] | null>(null)
  const [ready, setReady] = useState(true)
  /**
   * Whether answering these cards actually files anything — `null` until asked,
   * and `null` again if asking failed.
   *
   * IT IS THE DIFFERENCE BETWEEN A TRIAGE QUEUE AND A QUESTIONNAIRE. The origin
   * compiles the Screener's Sieve block only when `/screener/enabled` is true,
   * so with the switch off a household can work through this whole stack and
   * every message will keep landing exactly where it did before — and nothing
   * on this surface said so.
   */
  const [filing, setFiling] = useState<boolean | null>(null)
  const [confirming, setConfirming] = useState<PendingSender | null>(null)
  const [decisions, setDecisions] = useState<readonly KnownSender[] | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    api
      .screener()
      .then((page) => {
        setSenders(page.senders)
        setReady(page.ready)
      })
      .catch((error: unknown) => onError(error instanceof Error ? error.message : 'Could not open the Screener'))
  }, [onError])

  useEffect(() => {
    if (!open) return
    load()
  }, [open, load])

  // SILENT WHEN IT FAILS, like the shell's own count of who is waiting. This is
  // an explanation, not a feature: a household whose settings request 500s
  // should get the Screener it always had rather than an error over the cards.
  useEffect(() => {
    if (!open) return
    let live = true
    readScreenerEnabled()
      .then((enabled) => live && setFiling(enabled))
      .catch(() => live && setFiling(null))
    return () => {
      live = false
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        // ESCAPE CLOSES THE CONFIRM FIRST. Two dismissable layers and one key:
        // closing the whole surface out from under a confirm would leave
        // somebody unsure whether they had just trashed a sender.
        if (confirming) setConfirming(null)
        else onClose()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose, confirming])

  const decide = (sender: PendingSender, disposition: SenderDisposition): void => {
    setBusy(true)
    api
      .decideSender(sender.addr, disposition)
      .then(() => {
        // REMOVED LOCALLY RATHER THAN RE-FETCHED. The stack is a queue somebody
        // is working through, and re-fetching between cards would re-order it
        // under their hand every time a message arrived.
        setSenders((current) => (current ?? []).filter((s) => s.addr !== sender.addr))
        setConfirming(null)
        setDecisions(null)
        onDecided?.()
      })
      .catch((error: unknown) =>
        onError(error instanceof Error ? error.message : `Could not record that decision about ${sender.addr}`),
      )
      .finally(() => setBusy(false))
  }

  const undo = (addr: string): void => {
    api
      .forgetSender(addr)
      .then(() => {
        setDecisions((current) => (current ?? []).filter((d) => d.addr !== addr))
        load()
        onDecided?.()
      })
      .catch((error: unknown) => onError(error instanceof Error ? error.message : 'Could not undo that'))
  }

  if (!open) return null

  const card = senders?.[0] ?? null

  return (
    <div
      className="settings-scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !confirming) onClose()
      }}
    >
      <div className="settings-panel screener-panel" role="dialog" aria-modal="true" aria-label="Screener">
        <div className="settings-head">
          <h2 className="settings-title">Screener</h2>
          <button className="settings-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {/* ONLY WHEN THE ANSWER IS KNOWN AND IS NO, and only once the seed has
            found somebody: while `ready` is false the notice below already says
            what is happening, and two explanations of one state read as two
            problems. */}
        {ready && filing === false ? (
          <p className="screener-inert" role="status">
            Nothing is being filed yet — the Screener is switched off. Your answers are being recorded and will sort
            mail as soon as you turn it on, under Screener in Settings.
          </p>
        ) : null}

        {!ready ? (
          // THE STATE THE GATE CREATES, said plainly. The origin refuses to
          // switch the Screener on over an empty known-senders table; without
          // this the surface would just look broken.
          <p className="screener-empty">
            Hearth is still working out who you already know, from the mail you have sent. The Screener will start
            asking about new senders once that first sync has finished.
          </p>
        ) : senders === null ? (
          <p className="screener-empty">…</p>
        ) : card === null ? (
          <p className="screener-empty">
            Nobody new. Everything arriving is from someone you have already decided about.
          </p>
        ) : (
          <div className="screener-card">
            <p className="screener-count">
              {senders.length} {senders.length === 1 ? 'sender' : 'senders'} to decide about
            </p>
            <p className="screener-name">{card.name || card.addr}</p>
            {/* THE ADDRESS IS ALWAYS SHOWN BESIDE THE NAME, never instead of it.
                A display name is the one part of a message the sender chooses. */}
            <p className="screener-addr">{card.addr}</p>
            <p className="screener-meta">
              {card.messages} {card.messages === 1 ? 'message' : 'messages'}, first on {listDate(card.first_date)}
            </p>

            <div className="screener-answers">
              {SORTING.map((answer) => (
                <button
                  key={answer.value}
                  type="button"
                  className="screener-answer"
                  disabled={busy}
                  onClick={() => decide(card, answer.value)}
                >
                  <span className="screener-answer-label">{answer.label}</span>
                  <span className="screener-answer-hint">{answer.hint}</span>
                </button>
              ))}
            </div>

            <hr className="screener-rule" />

            <button type="button" className="screener-refuse" disabled={busy} onClick={() => setConfirming(card)}>
              No thanks — send future mail to Trash
            </button>
          </div>
        )}

        <details
          className="screener-decisions"
          onToggle={(event) => {
            if ((event.currentTarget as HTMLDetailsElement).open && decisions === null) {
              api
                .screenerDecisions()
                .then((r) => setDecisions(r.decisions))
                .catch(() => setDecisions([]))
            }
          }}
        >
          {/* THE SURFACE shell-ia PROMISES: "every decision is a visible,
              editable row". A standing rule you cannot see is one you cannot
              undo, and the Trash answer is the one that most needs undoing. */}
          <summary>Decisions you have made</summary>
          {decisions === null ? (
            <p className="screener-empty">…</p>
          ) : decisions.length === 0 ? (
            <p className="screener-empty">Nothing decided yet.</p>
          ) : (
            <ul className="screener-decision-list">
              {decisions.map((decision) => (
                <li key={decision.addr} data-disposition={decision.disposition}>
                  <span className="screener-decision-addr">{decision.addr}</span>
                  <span className="screener-decision-what">{DISPOSITION_LABEL[decision.disposition]}</span>
                  {/* A SEEDED ROW IS NOT A DECISION and is labelled as what it
                      is: Hearth worked it out from mail you had already sent.
                      Calling it a decision would be claiming somebody chose. */}
                  {decision.source === 'seed' ? <span className="screener-decision-src">worked out</span> : null}
                  <button type="button" className="screener-undo" onClick={() => undo(decision.addr)}>
                    Undo
                  </button>
                </li>
              ))}
            </ul>
          )}
        </details>
      </div>

      {confirming ? (
        <div className="settings-scrim screener-confirm-scrim">
          <div className="settings-panel screener-confirm" role="alertdialog" aria-modal="true" aria-label="Confirm">
            <p>
              Future mail from <strong>{confirming.addr}</strong> goes to Trash.
            </p>
            <p className="screener-confirm-note">You can undo this in the Screener’s decisions list.</p>
            <div className="compose-actions">
              <button type="button" className="compose-cancel" onClick={() => setConfirming(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="screener-confirm-go"
                disabled={busy}
                onClick={() => decide(confirming, 'trash')}
              >
                Send to Trash
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
